import { useEffect, useState } from 'react'

import { getArchive, restoreArchivedItem } from '../api/client'
import type { ArchivedItem } from '../api/client'

export interface ArchivePanelProps {
  boardId: number
  /** 当前 rev（每次恢复后由父组件刷新） */
  rev: number
  busy: boolean
  onClose: () => void
  /** 恢复成功后请求父组件重载看板 */
  onChanged: () => void
}

/**
 * 归档视图（M8）。
 *
 * 目前只展示「已归档条目」——分组级归档还没有接口，所以 groups 一般为空。
 * 每条可以「恢复」，恢复后该条目重新出现在看板里（ItemRepo 按 Archived==false 过滤）。
 */
export default function ArchivePanel({ boardId, rev, busy, onClose, onChanged }: ArchivePanelProps) {
  const [items, setItems] = useState<ArchivedItem[] | null>(null)
  const [err, setErr] = useState('')
  const [working, setWorking] = useState<number | null>(null)

  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const res = await getArchive(boardId)
        if (alive) setItems(res.items)
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => {
      alive = false
    }
  }, [boardId])

  const restore = async (id: number) => {
    setWorking(id)
    setErr('')
    try {
      await restoreArchivedItem(boardId, rev, id)
      setItems((prev) => (prev === null ? prev : prev.filter((x) => x.id !== id)))
      onChanged()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setWorking(null)
    }
  }

  return (
    <>
      <div className="archive-mask" onClick={onClose} />
      <div className="archive-panel" role="dialog" aria-modal="true" aria-label="查看归档">
        <header className="archive-panel__head">
          <span className="archive-panel__title">查看归档</span>
          <span className="archive-panel__hint">已归档的条目不会显示在看板里</span>
        </header>

        <div className="archive-panel__body">
          {err !== '' && <p className="archive-panel__err">{err}</p>}
          {items === null && err === '' && <p className="archive-panel__empty">加载中…</p>}
          {items !== null && items.length === 0 && (
            <p className="archive-panel__empty">暂无归档内容</p>
          )}
          {items !== null && items.length > 0 && (
            <table className="archive-table">
              <thead>
                <tr>
                  <th>条目</th>
                  <th>原分组</th>
                  <th aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.name}</td>
                    <td className="archive-table__dim">{it.groupTitle}</td>
                    <td className="archive-table__act">
                      <button
                        type="button"
                        className="archive-btn"
                        disabled={busy || working === it.id}
                        onClick={() => void restore(it.id)}
                      >
                        {working === it.id ? '恢复中…' : '恢复'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <footer className="archive-panel__foot">
          <button type="button" className="archive-btn" onClick={onClose}>
            关闭
          </button>
        </footer>
      </div>
    </>
  )
}
